import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { 
  CheckCircle2,
  ArrowRight
} from "lucide-react";
import basementWaterproofingParamus from "@/assets/basement-waterproofing-paramus-nj.webp";
import patioWaterproofingBergenCounty from "@/assets/patio-waterproofing-bergen-county-nj.webp";

const patioProtection = [
  "Sealer application to protect against water absorption",
  "Proper grading and drainage to direct water away",
  "Joint sand stabilization to prevent washout",
  "Protection from salt damage and freeze-thaw cycles",
];

const faqs = [
  {
    question: "Do you handle both interior and exterior waterproofing?",
    answer: "Yes. We assess where the water is entering and recommend the right method. Some jobs call for an interior drain and sump system. Others need exterior excavation and membrane work. We walk you through the options before any work starts.",
  },
  {
    question: "What areas do you serve for waterproofing?",
    answer: "We serve homeowners across Passaic, Bergen, Essex, and Morris counties. Our shop in Totowa puts us within a short drive of most North Jersey towns, and we can usually schedule a basement assessment within a few days of your call.",
  },
  {
    question: "How long does basement waterproofing last?",
    answer: "A properly installed system can last 20+ years. Sump pumps typically need replacement every 7-10 years. We use quality materials and stand behind our work.",
  },
  {
    question: "Do you offer warranties on waterproofing?",
    answer: "Yes. We provide written warranties on our waterproofing installations. The specific coverage depends on the system installed and site conditions.",
  },
];

export default function WaterproofingServices() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Waterproofing Service Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Waterproofing services in North Jersey. Basement waterproofing with French drains and sump pumps. Patio sealing and drainage. Free estimates offered today." />
        <meta property="og:description" content="Waterproofing services in North Jersey. Basement waterproofing with French drains and sump pumps. Patio sealing and drainage. Free estimates offered today." />
        <meta property="og:title" content="Waterproofing Service Totowa, NJ | Masonry Pro Construction" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/waterproofing-services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/waterproofing-services.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={basementWaterproofingParamus} alt="Basement waterproofing installation in Paramus, Bergen County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Waterproofing Service in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  From basement foundations to outdoor patios, we stop water damage at the source.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Get a Free Waterproofing Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Intro / Problem Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Water Problems <span className="text-gradient-gold">Don't Fix Themselves</span>
                </h2>
                
                <div className="space-y-4 text-lg text-muted-foreground mb-8">
                  <p>
                    Is water getting into your basement every time it rains? Are you seeing damp spots on the walls or smelling mildew you don't see?
                  </p>
                  <p>
                    Water problems don't go away on their own. What starts as a little moisture turns into puddles, then mold, and then damage you can't ignore. The longer you wait, the worse it gets, and the more expensive to fix.
                  </p>
                  <p>
                    We've been fixing water issues in North Jersey for over 35 years. We know the soil, the water table, and what works in this climate. And we give you honest advice about what you actually need. Waterproofing grew naturally out of our foundation work as a <Link to="/" className="text-secondary hover:underline">Totowa masonry contractor</Link>, since keeping water out starts with the walls themselves.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Basement Waterproofing Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    <Link to="/waterproofing-services/basement-waterproofing" className="hover:text-secondary transition-colors">
                      Basement Waterproofing <span className="text-gradient-gold">Done Right</span>
                    </Link>
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    Water in your basement usually means one of two things: water is getting through the foundation wall, or it is coming up through the floor. We figure out which one and fix it. Interior drain systems, exterior membranes, sump pumps with battery backup, crack injection. The right approach depends on where the water is coming from and what the soil around your foundation is doing. See how <Link to="/waterproofing-services/basement-waterproofing" className="text-secondary hover:underline">basement waterproofing</Link> works on North Jersey homes, inside and out.
                  </p>

                  <Button variant="goldOutline" asChild>
                    <Link to="/waterproofing-services/basement-waterproofing" className="flex items-center gap-2">
                      Learn More About Basement Waterproofing
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>

                <div className="overflow-hidden rounded-xl border border-border">
                  <img
                    src={basementWaterproofingParamus}
                    alt="Basement waterproofing in progress with gravel and waterproof sheeting around a foundation in Paramus, Bergen County, NJ."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Patio Waterproofing Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto items-center">
                <div className="order-2 md:order-1 overflow-hidden rounded-xl border border-border">
                  <img
                    src={patioWaterproofingBergenCounty}
                    alt="Newly installed patio pavers with green grass in Emerson, Bergen County, NJ, ready for patio waterproofing by Masonry Pro."
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                <div className="order-1 md:order-2">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">
                    <Link to="/waterproofing-services/patio-waterproofing" className="hover:text-secondary transition-colors">
                      Patio <span className="text-gradient-gold">Waterproofing & Sealing</span>
                    </Link>
                  </h2>
                  
                  <div className="space-y-4 text-lg text-muted-foreground mb-6">
                    <p>
                      We seal and protect your paver or stone patio from water damage, salt, and the freeze-thaw cycle. Proper drainage keeps your outdoor investment looking great for years.
                    </p>
                    <p>
                      North Jersey winters are tough on outdoor surfaces. Water gets into pores and joints, freezes, expands, and cracks your investment. Proper sealing and drainage prevent that damage before it starts. Learn how <Link to="/waterproofing-services/patio-waterproofing" className="text-secondary hover:underline">patio waterproofing</Link> protects pavers and stone from salt and freeze-thaw.
                    </p>
                  </div>

                  <div className="bg-background rounded-xl p-6 border border-border mb-8">
                    <h3 className="text-xl font-bold mb-4">Our Patio Protection Services</h3>
                    <ul className="space-y-3">
                      {patioProtection.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button variant="goldOutline" asChild>
                    <Link to="/waterproofing-services/patio-waterproofing" className="flex items-center gap-2">
                      Learn More About Patio Waterproofing
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {faqs.map((faq) => (
                  <div key={faq.question} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="waterproofing" />

          <CTASection />
        </main>

        <Footer />
        <AccessibilityWidget />
      </div>
    </>
  );
}
